/**
 * EntityTableView — dense Excel-style table for browsing companies,
 * investors, or philanthropies. Caller passes:
 *   - rows: any[]
 *   - columns: list of { key, label, render?(row), align? }
 *   - onRowClick?(row)  → navigate to detail page
 *
 * Copy-friendly (text selection works), sticky header, and a CSV
 * download button. Designed as a side-by-side toggle with the card
 * grid each list page already renders.
 */
import React, { useMemo, useState } from 'react';
import { Download, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';

export interface TableColumn<T> {
  key: string;
  label: string;
  align?: 'left' | 'right' | 'center';
  width?: string;  // tailwind class, e.g. "w-32"
  render?: (row: T) => React.ReactNode;
  csv?: (row: T) => string | number | null | undefined;
  // When true (default), clicking the header sorts the table by this
  // column. Pass ``sortable: false`` to opt out (useful for action /
  // icon columns where ordering is meaningless).
  sortable?: boolean;
  // Override how this column extracts its sortable value. Defaults to
  // ``row[key]`` — but if the rendered cell is a ReactNode you'll want
  // to point at the underlying scalar.
  sortValue?: (row: T) => string | number | null | undefined;
}

interface Props<T> {
  rows: T[];
  columns: TableColumn<T>[];
  onRowClick?: (row: T) => void;
  csvFilename?: string;
}

type SortDirection = 'asc' | 'desc';

function compareValues(
  a: string | number | null | undefined,
  b: string | number | null | undefined,
): number {
  // Push null/undefined to the bottom regardless of direction.
  const aMissing = a === null || a === undefined || a === '';
  const bMissing = b === null || b === undefined || b === '';
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

function csvEscape(v: unknown): string {
  if (v === null || v === undefined) return '';
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadCsv<T>(filename: string, columns: TableColumn<T>[], rows: T[]) {
  const header = columns.map((c) => csvEscape(c.label)).join(',');
  const body = rows
    .map((r) =>
      columns
        .map((c) => {
          const raw = c.csv ? c.csv(r) : (r as any)[c.key];
          return csvEscape(raw);
        })
        .join(','),
    )
    .join('\n');
  const blob = new Blob([header + '\n' + body], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function EntityTableView<T>({
  rows,
  columns,
  onRowClick,
  csvFilename = 'export.csv',
}: Props<T>) {
  // Click header to sort. First click → asc, second → desc, third → off.
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDirection>('asc');

  const onHeaderClick = (col: TableColumn<T>) => {
    if (col.sortable === false) return;
    if (sortKey !== col.key) {
      setSortKey(col.key);
      setSortDir('asc');
      return;
    }
    if (sortDir === 'asc') {
      setSortDir('desc');
      return;
    }
    // Third click — clear sort.
    setSortKey(null);
    setSortDir('asc');
  };

  const sortedRows = useMemo(() => {
    if (!sortKey) return rows;
    const col = columns.find((c) => c.key === sortKey);
    if (!col) return rows;
    const extract = col.sortValue || ((r: T) => (r as any)[sortKey] as any);
    const copy = rows.slice();
    copy.sort((a, b) => {
      const cmp = compareValues(extract(a), extract(b));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [rows, columns, sortKey, sortDir]);

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-gray-50">
        <span className="text-xs text-gray-500">
          {rows.length} row{rows.length === 1 ? '' : 's'}
          {sortKey && (
            <span className="ml-2 text-gray-400">
              · sorted by <strong>{columns.find((c) => c.key === sortKey)?.label}</strong> ({sortDir})
            </span>
          )}
        </span>
        <button
          type="button"
          onClick={() => downloadCsv(csvFilename, columns, sortedRows)}
          className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-gray-200 bg-white hover:bg-gray-100"
          title="Download visible rows as CSV"
        >
          <Download className="w-3 h-3" />
          CSV
        </button>
      </div>
      <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="sticky top-0 bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              {columns.map((c) => {
                const isSorted = sortKey === c.key;
                const isSortable = c.sortable !== false;
                return (
                  <th
                    key={c.key}
                    onClick={isSortable ? () => onHeaderClick(c) : undefined}
                    className={`px-3 py-2 font-semibold whitespace-nowrap text-${c.align || 'left'} ${c.width || ''} ${isSortable ? 'cursor-pointer select-none hover:bg-gray-100' : ''}`}
                    title={isSortable ? `Sort by ${c.label}` : undefined}
                  >
                    <span className="inline-flex items-center gap-1">
                      <span>{c.label}</span>
                      {isSortable && (
                        isSorted ? (
                          sortDir === 'asc'
                            ? <ArrowUp className="w-3 h-3 text-blue-600" />
                            : <ArrowDown className="w-3 h-3 text-blue-600" />
                        ) : <ArrowUpDown className="w-3 h-3 text-gray-300" />
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedRows.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="px-3 py-6 text-center text-sm text-gray-500">
                  No rows match the current filters.
                </td>
              </tr>
            )}
            {sortedRows.map((r, i) => (
              <tr
                key={(r as any).id ?? i}
                onClick={onRowClick ? () => onRowClick(r) : undefined}
                className={`border-b border-gray-100 last:border-b-0 ${onRowClick ? 'cursor-pointer hover:bg-blue-50/40' : ''}`}
              >
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={`px-3 py-2 text-${c.align || 'left'} ${c.width || ''}`}
                  >
                    {c.render ? c.render(r) : ((r as any)[c.key] ?? '—')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default EntityTableView;
